/**
 * Site-wide outage / maintenance notice.
 *
 * The notice is driven by the `outage_notice` parameter in Firebase Remote
 * Config, so switching it on or off is an edit in the Firebase console
 * followed by "Publish changes". No code change, no rebuild and no deploy.
 *
 * Build-time environment variables act as a fallback for when Remote Config
 * cannot be reached, so a notice can still be baked into a build.
 *
 * The config contract is shared with the learner app
 * (reflective-learning-client-vue). Keep the two in step.
 */

import { fetchAndActivate, getRemoteConfig, getString } from "firebase/remote-config";

import { firebaseApp } from "@/utils/firebase";

export type OutageSeverity = "critical" | "warning" | "info";

export interface OutageNoticeConfig {
  /** Changing the id makes the notice reappear for users who dismissed the previous one. */
  id: string;
  enabled: boolean;
  severity: OutageSeverity;
  dismissible: boolean;
  /** ISO 8601. Null means "already started". */
  startsAt: string | null;
  /** ISO 8601. Null means "no end". */
  endsAt: string | null;
  message: string;
  /** Optional per-locale overrides, keyed by locale code. */
  messages: Record<string, string>;
}

export interface ResolvedOutageNotice {
  id: string;
  message: string;
  severity: OutageSeverity;
  dismissible: boolean;
}

export interface ResolveOptions {
  now?: Date;
  locale?: string | null;
  dismissedId?: string | null;
}

/** The Remote Config parameter holding the notice, as a JSON value. */
export const REMOTE_CONFIG_KEY = "outage_notice";

/**
 * Open sessions re-check on this interval, so ending an outage clears the
 * banner without a reload. Also the Remote Config minimum fetch interval:
 * checks in between are served from the SDK's cache.
 */
export const POLL_INTERVAL_MS = 5 * 60 * 1000;

export const DISMISSAL_STORAGE_KEY = "outage-notice-dismissed";

const SEVERITIES: OutageSeverity[] = ["critical", "warning", "info"];

const asString = (value: unknown): string => (typeof value === "string" ? value.trim() : "");

const asBoolean = (value: unknown, fallback: boolean): boolean => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    const normalised = value.trim().toLowerCase();

    if (["true", "1", "yes", "on"].includes(normalised)) {
      return true;
    }

    if (["false", "0", "no", "off"].includes(normalised)) {
      return false;
    }
  }

  return fallback;
};

const asSeverity = (value: unknown): OutageSeverity => {
  const candidate = asString(value).toLowerCase() as OutageSeverity;

  return SEVERITIES.includes(candidate) ? candidate : "critical";
};

/** Unparseable timestamps are treated as "not set" rather than hiding the notice. */
const asTimestamp = (value: unknown): string | null => {
  const candidate = asString(value);

  if (!candidate || Number.isNaN(Date.parse(candidate))) {
    return null;
  }

  return candidate;
};

const asMessages = (value: unknown): Record<string, string> => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return {};
  }

  return Object.entries(value as Record<string, unknown>).reduce((messages, [locale, message]) => {
    const text = asString(message);

    if (text) {
      messages[locale] = text;
    }

    return messages;
  }, {} as Record<string, string>);
};

/**
 * Coerce whatever the parameter contains into a usable config. Returns null
 * when there is no message to show, so a malformed value behaves like "off"
 * rather than rendering an empty banner.
 */
export const normaliseNotice = (raw: unknown): OutageNoticeConfig | null => {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return null;
  }

  const source = raw as Record<string, unknown>;
  const message = asString(source.message);
  const messages = asMessages(source.messages);

  if (!message && Object.keys(messages).length === 0) {
    return null;
  }

  return {
    id: asString(source.id) || "outage-notice",
    enabled: asBoolean(source.enabled, false),
    severity: asSeverity(source.severity),
    dismissible: asBoolean(source.dismissible, true),
    startsAt: asTimestamp(source.startsAt),
    endsAt: asTimestamp(source.endsAt),
    message,
    messages,
  };
};

/**
 * Build-time fallback, used when Remote Config cannot be read or leaves the
 * notice unset. Also how the banner is exercised locally, without needing
 * console access to the dev project.
 */
export const envNotice = (): OutageNoticeConfig | null =>
  normaliseNotice({
    id: process.env.VUE_APP_OUTAGE_NOTICE_ID,
    enabled: process.env.VUE_APP_OUTAGE_NOTICE_ENABLED,
    severity: process.env.VUE_APP_OUTAGE_NOTICE_SEVERITY,
    dismissible: process.env.VUE_APP_OUTAGE_NOTICE_DISMISSIBLE,
    message: process.env.VUE_APP_OUTAGE_NOTICE_MESSAGE,
  });

const localisedMessage = (config: OutageNoticeConfig, locale?: string | null): string => {
  const requested = asString(locale);

  if (requested) {
    const base = requested.split("-")[0];

    return config.messages[requested] || config.messages[base] || config.message;
  }

  return config.message;
};

const withinWindow = (config: OutageNoticeConfig, now: Date): boolean => {
  const timestamp = now.getTime();

  if (config.startsAt && timestamp < Date.parse(config.startsAt)) {
    return false;
  }

  if (config.endsAt && timestamp >= Date.parse(config.endsAt)) {
    return false;
  }

  return true;
};

/** Returns the notice to display, or null when nothing should be shown. */
export const resolveNotice = (
  config: OutageNoticeConfig | null,
  { now = new Date(), locale = null, dismissedId = null }: ResolveOptions = {}
): ResolvedOutageNotice | null => {
  if (!config || !config.enabled || !withinWindow(config, now)) {
    return null;
  }

  // Dismissal is tied to the notice id, so publishing a new notice reaches
  // users who dismissed the previous one.
  if (config.dismissible && dismissedId === config.id) {
    return null;
  }

  const message = localisedMessage(config, locale);

  if (!message) {
    return null;
  }

  return {
    id: config.id,
    message,
    severity: config.severity,
    dismissible: config.dismissible,
  };
};

/** Reads the raw parameter value. Injectable so tests do not need the SDK. */
export type RemoteConfigReader = () => Promise<string>;

const readFromRemoteConfig: RemoteConfigReader = async () => {
  const remoteConfig = getRemoteConfig(firebaseApp);

  // Without a default the SDK logs a warning on every miss, and the fetch
  // interval otherwise defaults to 12 hours.
  remoteConfig.defaultConfig = { [REMOTE_CONFIG_KEY]: "" };
  remoteConfig.settings.minimumFetchIntervalMillis = POLL_INTERVAL_MS;

  await fetchAndActivate(remoteConfig);

  return getString(remoteConfig, REMOTE_CONFIG_KEY);
};

/**
 * Read the notice from Remote Config. Returns null on any failure (offline,
 * Remote Config unreachable, parameter unset or malformed) so the caller can
 * fall back to the build-time config.
 */
export const fetchNotice = async (
  read: RemoteConfigReader = readFromRemoteConfig
): Promise<OutageNoticeConfig | null> => {
  try {
    const raw = asString(await read());

    if (!raw) {
      return null;
    }

    return normaliseNotice(JSON.parse(raw));
  } catch (error) {
    return null;
  }
};

export const readDismissedId = (): string | null => {
  try {
    return window.sessionStorage.getItem(DISMISSAL_STORAGE_KEY);
  } catch (error) {
    return null;
  }
};

export const writeDismissedId = (id: string): void => {
  try {
    window.sessionStorage.setItem(DISMISSAL_STORAGE_KEY, id);
  } catch (error) {
    // Storage unavailable (private browsing); the dismissal simply is not persisted.
  }
};
